import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, ShieldAlert, Download, Trash2, Archive, FileSpreadsheet, Loader2, History } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BulkDeleteDialog, DeleteMaterialDialog, SkippedSheet } from "@/components/MaterialDelete";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, download, errMsg, fmtNum } from "@/lib/api";
import { toast } from "sonner";
import { usePlant } from "@/context/PlantContext";
import { PageHeader, ScopeBadge, SCOPE } from "@/components/common";
import DataTable, { useDebounced } from "@/components/DataTable";
import { ActionBanner } from "@/components/ActionBanner";
import MaterialDrawer from "@/pages/MaterialDrawer";
import { cn } from "@/lib/utils";
import { SourcingBadge, SourcingSelect } from "@/components/ProductRequest";
import { CodeBadge, CodeBulkDialog } from "@/components/CodeEdit";

const COMMODITIES = ["Cattle", "Cocoa", "Coffee", "Oil palm", "Rubber", "Soya", "Wood"];

export default function Materials() {
  const [sp, setSp] = useSearchParams();
  const { params: plantParams, plants } = usePlant();
  const [q, setQ] = useState(sp.get("q") || "");
  const [scope, setScope] = useState(sp.get("scope") || "all");
  const [plant, setPlant] = useState(sp.get("plant") || "global");
  const [stock, setStock] = useState(sp.get("stock") || (sp.get("action") ? "in" : "all"));
  const [sourcing, setSourcing] = useState(sp.get("sourcing") || "");
  const action = sp.get("action") || "";
  useEffect(() => {
    const n = Object.fromEntries(Object.entries({ q: q || "", scope: scope === "all" ? "" : scope, plant: plant === "global" ? "" : plant, stock: stock === "all" && !action ? "" : stock, sourcing, action }).filter(([, v]) => v));
    setSp(n, { replace: true });
  }, [q, scope, plant, stock, sourcing]); // eslint-disable-line react-hooks/exhaustive-deps
  const [commodity, setCommodity] = useState("all");
  const [status, setStatus] = useState("all");
  const [overridden, setOverridden] = useState("all");
  const [facets, setFacets] = useState({ scope: {}, status: [] });
  const [open, setOpen] = useState(null);
  const [reload, setReload] = useState(0);
  const dq = useDebounced(q);
  const { can } = useAuth();
  const [del, setDel] = useState(null);
  const [bulk, setBulk] = useState(false);
  const [skipped, setSkipped] = useState(false);
  const [codeBulk, setCodeBulk] = useState(false);
  const [sortState, setSortState] = useState({ key: null, dir: "asc" });
  const [xlsxBusy, setXlsxBusy] = useState(false);
  const changed = () => setReload((x) => x + 1);

  const pp = useMemo(() => (plant === "global" ? plantParams : { plant }), [plant, plantParams]);

  useEffect(() => {
    if (can.accountManager) return;
    api.get("/materials/facets", { params: pp }).then((r) => setFacets(r.data));
  }, [pp, reload]);

  const params = { ...pp, q: dq, scope, commodity, status, stock: stock === "all" && !action ? undefined : stock, sourcing: sourcing || undefined, action: action || undefined, overridden: ["all", "code_diff"].includes(overridden) ? undefined : overridden === "yes", code_diff: overridden === "code_diff" || undefined };
  const exportCsv = async () => {
    const r = await api.get("/materials", { params: { ...params, scope: scope === "all" ? undefined : scope, commodity: commodity === "all" ? undefined : commodity, status: status === "all" ? undefined : status, format: "csv" }, responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data); a.download = `materials_${plant}_${scope}_${stock}.csv`; a.click();
  };
  const exportHistory = () => {
    const p = { ...params, scope: scope === "all" ? "" : scope, commodity: commodity === "all" ? "" : commodity, status: status === "all" ? "" : status };
    const qs = new URLSearchParams(Object.entries(p).filter(([, v]) => v !== undefined && v !== null && v !== "").map(([k, v]) => [k, String(v)]));
    download(`/materials/code-history.xlsx?${qs}`, "code_history.xlsx").catch((e) => toast.error(errMsg(e)));
  };
  const exportXlsx = async () => {
    setXlsxBusy(true);
    try {
      const r = await api.get("/materials", { params: { ...params, scope: scope === "all" ? undefined : scope, commodity: commodity === "all" ? undefined : commodity, status: status === "all" ? undefined : status, sort: sortState.key || undefined, dir: sortState.dir, format: "xlsx" }, responseType: "blob", timeout: 120000 });
      const name = (r.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "materials.xlsx";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = name; a.click();
    } finally { setXlsxBusy(false); }
  };
  const total = Object.values(facets.scope).reduce((a, b) => a + b, 0);

  const columns = [
    { key: "material", label: "Material", sortable: true, render: (r) => <span className="font-mono text-[13px] font-medium text-slate-900">{r.material}</span> },
    { key: "plant", label: "Plant", sortable: true, render: (r) => <span className="font-mono text-xs">{r.plant}</span> },
    { key: "description", label: "Description", sortable: true, className: "max-w-[340px] truncate", render: (r) => <span title={r.description}>{r.description || "—"}</span> },
    { key: "commodity_code", label: "Commodity code", sortable: true, render: (r) => <span className="font-mono text-xs">{r.commodity_code || "—"}<CodeBadge r={r} testid={`code-badge-${r.material}-${r.plant}`} /></span> },
    { key: "scope", label: "EUDR scope", sortable: true, render: (r) => <ScopeBadge scope={r.scope} from={r.applies_from} overridden={!!r.override} testid={`material-scope-${r.material}-${r.plant}`} /> },
    { key: "sourcing", label: "Sourcing", render: (r) => <SourcingBadge s={r.sourcing} testid={`material-sourcing-${r.material}-${r.plant}`} /> },
    { key: "commodity", label: "Commodity", sortable: true, render: (r) => r.commodity || "—" },
    { key: "annex_code", label: "Annex I match", render: (r) => (r.annex_code ? <span className="font-mono text-xs text-slate-500">{r.annex_code}</span> : "—") },
    { key: "stock_qty", label: "Stock qty", sortable: true, className: "text-right", thClass: "text-right", render: (r) => <span data-testid={`material-stock-${r.material}-${r.plant}`} title={r.stock_source === "stock" ? `Stock upload · £${fmtNum(Math.round(r.stock_value_gbp || 0))}${r.stock_converted ? " (converted)" : ""}` : "Materials file Unrestricted Stock"}>{fmtNum(r.stock_qty ?? r.unrestricted_stock)}</span> },
    { key: "status", label: "Status", render: (r) => r.status || r.plant_status || "—" },
    ...(can.override ? [{ key: "actions", label: "", render: (r) => (
      <button title="Delete or mark not required" data-testid={`material-delete-${r.material}-${r.plant}`} onClick={(e) => { e.stopPropagation(); setDel(r); }}
        className="rounded p-1 text-slate-400 transition-colors duration-150 hover:bg-rose-50 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>) }] : []),
  ];
  const filters = { ...pp, q: dq || undefined, scope: scope === "all" ? undefined : scope, commodity: commodity === "all" ? undefined : commodity, status: status === "all" ? undefined : status,
    stock: stock === "all" ? undefined : stock, sourcing: sourcing || undefined, overridden: ["all", "code_diff"].includes(overridden) ? undefined : overridden === "yes", code_diff: overridden === "code_diff" || undefined };

  return (
    <div data-testid="materials-page">
      <PageHeader title="Materials catalogue" subtitle="Every material per plant, matched to Annex I by longest HS-code prefix. Click a row for stock, vendors and PO lines." />
      <ActionBanner action={action} clearTo="/materials" />
      <div className="mb-4 flex flex-wrap gap-2 fade-up" data-testid="scope-tabs">
        {["all", "in_scope", "check", "missing_code", "out_of_scope", "future", "not_required"].map((s) => (
          <button
            key={s}
            data-testid={`scope-filter-${s}`}
            onClick={() => setScope(s)}
            className={cn(
              "flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors duration-150",
              scope === s ? "border-[#1C1C1E] bg-[#1C1C1E] text-white" : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
            )}
          >
            {s !== "all" && <span className="h-2 w-2 rounded-full" style={{ background: SCOPE[s].color }} />}
            {s === "all" ? "All" : SCOPE[s].label}
            <span className={cn("text-xs", scope === s ? "text-slate-300" : "text-slate-400")}>{fmtNum(s === "all" ? total : facets.scope[s] || 0)}</span>
          </button>
        ))}
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input data-testid="materials-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search material, description or code…" className="h-9 bg-white pl-9" />
        </div>
        <Select value={plant} onValueChange={setPlant}>
          <SelectTrigger className="h-9 w-[160px] bg-white" data-testid="materials-plant-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="global">Plant: global</SelectItem>
            {plants.map((p) => <SelectItem key={p.code} value={p.code} data-testid={`materials-plant-option-${p.code}`}>Plant {p.code}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={commodity} onValueChange={setCommodity}>
          <SelectTrigger className="h-9 w-[160px] bg-white" data-testid="materials-commodity-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All commodities</SelectItem>
            {COMMODITIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-[150px] bg-white" data-testid="materials-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any SAP status</SelectItem>
            <SelectItem value="none">No status</SelectItem>
            {facets.status.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={overridden} onValueChange={setOverridden}>
          <SelectTrigger className="h-9 w-[160px] bg-white" data-testid="materials-override-filter">
            <ShieldAlert className="mr-1 h-3.5 w-3.5 text-slate-400" /><SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any source</SelectItem>
            <SelectItem value="yes">Overridden</SelectItem>
            <SelectItem value="no">Auto-matched</SelectItem>
            <SelectItem value="code_diff" data-testid="materials-code-diff-option">Code differs from SAP</SelectItem>
          </SelectContent>
        </Select>
        <Select value={stock} onValueChange={setStock}>
          <SelectTrigger className="h-9 w-[150px] bg-white" data-testid="materials-stock-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="materials-stock-option-all">Stock: all</SelectItem>
            <SelectItem value="in" data-testid="materials-stock-option-in">In stock (&gt; 0)</SelectItem>
            <SelectItem value="zero" data-testid="materials-stock-option-zero">Zero stock</SelectItem>
          </SelectContent>
        </Select>
        <SourcingSelect value={sourcing} onChange={setSourcing} testid="materials-sourcing-filter" />
        <Button variant="outline" size="sm" className="h-9" data-testid="materials-code-history-btn" onClick={exportHistory}><History className="mr-1.5 h-3.5 w-3.5" />Code history</Button>
        <Button variant="outline" size="sm" className="h-9" data-testid="materials-export-btn" onClick={exportCsv}><Download className="mr-1.5 h-3.5 w-3.5" />Export CSV</Button>
        <Button variant="outline" size="sm" className="h-9" data-testid="materials-export-xlsx-btn" onClick={exportXlsx} disabled={xlsxBusy}>{xlsxBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <FileSpreadsheet className="mr-1.5 h-3.5 w-3.5" />}Export to Excel</Button>
        {can.upload && <Button variant="outline" size="sm" className="h-9" data-testid="materials-code-upload-btn" onClick={() => setCodeBulk(true)}>Upload code corrections</Button>}
        <Button variant="outline" size="sm" className="h-9" data-testid="materials-skipped-btn" onClick={() => setSkipped(true)}><Archive className="mr-1.5 h-3.5 w-3.5" />Skipped / Not required</Button>
        {can.override && <Button variant="outline" size="sm" className="h-9 border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="materials-delete-all-btn" onClick={() => setBulk(true)}><Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete all in view</Button>}
      </div>
      <DataTable endpoint="/materials" params={params} columns={columns} onSortChange={setSortState} onRowClick={can.accountManager ? undefined : (r) => setOpen(r.material)} testid="materials-table" reloadKey={reload} />
      <MaterialDrawer material={open} onClose={() => setOpen(null)} onChanged={changed} />
      <CodeBulkDialog open={codeBulk} onClose={() => setCodeBulk(false)} onDone={() => { setCodeBulk(false); changed(); }} />
      <DeleteMaterialDialog rec={del} onClose={() => setDel(null)} onDone={() => { setDel(null); changed(); }} />
      <BulkDeleteDialog open={bulk} filters={filters} onClose={() => setBulk(false)} onDone={() => { setBulk(false); changed(); }} />
      <SkippedSheet open={skipped} onClose={() => setSkipped(false)} onChanged={changed} canEdit={can.override} />
    </div>
  );
}
